import React, { Dispatch, SetStateAction } from "react"
import dayjs from "dayjs"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import styled from "styled-components"
import { useAppDispatch } from "lib/store"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { SubmitHandler, useForm } from "react-hook-form"
import { useMutation, useQuery } from "@apollo/client"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { PROVIDER_CATEGORY, PROVIDER_TYPE } from "lib/types/common"

import GET_CUSTOM_GIFT_CARD_CONFIG from "graphql/queries/config/GetCustomGiftCardConfig"
import CREATE_OR_UPDATE_CUSTOM_GIFT_CARD_CONFIG from "graphql/mutations/config/CreateOrUpdateCustomGiftCardConfig"
import DELETE_CUSTOM_GIFT_CARD_CONFIG from "graphql/mutations/config/DeleteCustomGiftCardConfig"

import EditSidebar from "components/Ui/EditSidebar/EditSidebar"
import EditSidebarHeader from "components/Ui/EditSidebar/EditSidebarHeader"
import DeleteButton from "components/Ui/EditSidebar/DeleteButton"
import PrimaryButton from "components/Ui/Buttons/PrimaryButton"
import CustomGiftCardConfigInputs, {
  ExtendedInputCustomGiftCardConfig
} from "components/Config/CustomGiftCard/CustomGiftCardConfigInputs"
import ConnectedStoreMarket from "components/Config/ConnectedStoreMarkets/ConnectedStoreMarket"

import { ReactComponent as CardIcon } from "images/icons/credit-card.svg"
import { WithAuth } from "../../../../components/Auth"
import { PermissionGroups } from "../../../../routes/constants"

const Label = styled.label`
  font-weight: 700;
  display: flex;
  align-items: center;
  padding-bottom: 0.3rem;
`

const Info = styled.div`
  padding-bottom: 2rem;
`

type Props = {
  refetch: () => void
  editId: string
  setEditId: Dispatch<SetStateAction<string>>
}

export const EditCustomGiftCardConfig = ({ refetch, editId, setEditId }: Props) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<ExtendedInputCustomGiftCardConfig>({ mode: "onBlur" })

  const { data, loading } = useQuery(GET_CUSTOM_GIFT_CARD_CONFIG, {
    variables: { id: editId }
  })

  const customConfig = data?.getBSGGiftCardConfig

  const dispatch = useAppDispatch()
  const [updateCustomGiftCardConfig, { loading: updateLoading }] = useMutation(
    CREATE_OR_UPDATE_CUSTOM_GIFT_CARD_CONFIG,
    {
      onCompleted: () => {
        dispatch(
          alertActions.actions.createAlert({
            message: "Brink Custom Gift Card configuration successfully updated",
            type: "success"
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )

  const onSubmit: SubmitHandler<ExtendedInputCustomGiftCardConfig> = (data) => {
    updateCustomGiftCardConfig({
      variables: {
        id: customConfig.id,
        baseUrl: data.baseUrl,
        tokenUrl: data.tokenUrl,
        scopes: data.scopes,
        clientId: data.clientId,
        clientSecret: data.clientSecret
      }
    })
  }

  const closeSidebar = () => {
    setEditId("")
    dispatch(hideEditSidebar())
  }

  if (loading) return <EditSidebar loading={loading} />

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <EditSidebar setEditId={setEditId}>
        <EditSidebarHeader
          icon={<CardIcon />}
          title={customConfig?.id}
          id={customConfig?.id}
          cancelEvent={() => closeSidebar()}
        >
          <WithAuth requiredGroups={PermissionGroups.providers.write}>
            <DeleteButton
              id={customConfig?.id}
              name={customConfig?.id}
              entity={"Brink Custom Gift Card configuration"}
              redirectUrl="/settings/providers/bsg-gift-card"
              mutation={DELETE_CUSTOM_GIFT_CARD_CONFIG}
              refetch={refetch}
              postDeleteFunction={() => setEditId("")}
              secureDelete={true}
            />
            <PrimaryButton type="submit" loading={updateLoading}>
              Save
            </PrimaryButton>
          </WithAuth>
        </EditSidebarHeader>
        <Tabs>
          <TabList>
            <Tab>General</Tab>
            <Tab>Connected store markets</Tab>
          </TabList>
          <TabPanel>
            <Label>Created:</Label>
            <Info>{dayjs(customConfig.created).format("YYYY-MM-DD")}</Info>
            <Label>Updated:</Label>
            <Info>{dayjs(customConfig.updated).format("YYYY-MM-DD")}</Info>
            <CustomGiftCardConfigInputs
              control={control}
              errors={errors}
              setValue={setValue}
              defaultValues={customConfig}
            />
          </TabPanel>
          <TabPanel>
            <ConnectedStoreMarket
              providerId={customConfig.id}
              providerType={PROVIDER_TYPE.BSG_GIFT_CARD}
              providerCategory={PROVIDER_CATEGORY.GIFTCARD}
            />
          </TabPanel>
        </Tabs>
      </EditSidebar>
    </form>
  )
}
